/**
 * PR-14.25: Wompi Card Tokenization Component
 *
 * SAQ-A INVARIANTS:
 * [INVARIANT 12] Tokenization endpoint from Wompi docs:
 *   POST {baseUrl}/v1/tokens/cards
 *   Authorization: Bearer {public_key}
 *   Body: { number, cvc, exp_month, exp_year, card_holder }
 *   Response: { id, status, brand, last_four, exp_month, exp_year }
 *
 * [INVARIANT 13] Card fields MUST NOT be stored in React state.
 *   Inputs are UNCONTROLLED — values exist only in the DOM.
 *   On submit, we read values once via FormData, POST to Wompi, then discard immediately.
 *   Only the returned token id + safe metadata (last4, brand) enter component state.
 *
 * [INVARIANT 14] 30-second timeout — no infinite pending state.
 *
 * [INVARIANT 17] Tokenization errors are mapped to user-safe messages.
 */

import { useRef, useState, useEffect } from 'react';
import type { CardTokenResult } from '@/lib/state-machine';

interface WompiTokenizationProps {
  /** Wompi public key — tenant-scoped, environment-scoped. NOT a secret. */
  publicKey: string;
  environment: 'sandbox' | 'production';
  /** Persisted error from a previous attempt (stored in state machine, not local state) */
  persistentError?: string;
  onSuccess: (result: CardTokenResult) => void;
  /** Called with a user-safe message — no raw provider error exposed */
  onError: (safeMessage: string) => void;
  onCancel: () => void;
}

/** [INVARIANT 17] Map raw error signals to user-safe messages */
function mapTokenizationError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (/declined|DECLINED/.test(msg)) return 'La tarjeta fue rechazada. Por favor intenta con otra tarjeta.';
  if (/expired|EXPIRED/.test(msg)) return 'Esta tarjeta está vencida. Por favor intenta con otra tarjeta.';
  if (/invalid|INVALID/.test(msg)) return 'Información de tarjeta no válida. Por favor verifica los datos.';
  if (/timeout|TIMEOUT/.test(msg)) return 'La solicitud expiró. Por favor intenta de nuevo.';
  if (/network|fetch/.test(msg)) return 'Error de conexión. Verifica tu internet e intenta de nuevo.';
  // Surface raw message in dev so we can debug API errors
  if (import.meta.env.DEV) return `Error: ${msg}`;
  return 'No se pudo procesar la tarjeta. Intenta de nuevo o usa otro método de pago.';
}

/** Detect card brand from first digits */
function detectBrand(digits: string): 'visa' | 'mastercard' | 'amex' | 'unknown' {
  if (/^4/.test(digits)) return 'visa';
  if (/^(5[1-5]|2[2-7])/.test(digits)) return 'mastercard';
  if (/^3[47]/.test(digits)) return 'amex';
  return 'unknown';
}

/** Format card number: Amex = 4-6-5, others = 4-4-4-4 */
function formatCardNumber(raw: string, brand: string): string {
  const digits = raw.replace(/\D/g, '');
  if (brand === 'amex') {
    return digits.replace(/^(\d{0,4})(\d{0,6})(\d{0,5}).*/, (_, a, b, c) =>
      [a, b, c].filter(Boolean).join(' ')
    );
  }
  return digits.replace(/(.{4})/g, '$1 ').trim();
}

export function WompiCardTokenization({
  publicKey,
  environment,
  persistentError,
  onSuccess,
  onError,
  onCancel,
}: WompiTokenizationProps) {
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  // Card number display state — formatted digits only (no CVV/expiry stored in state)
  const [cardDisplay, setCardDisplay] = useState('');
  const [brand, setBrand] = useState<'visa' | 'mastercard' | 'amex' | 'unknown'>('unknown');
  const formRef = useRef<HTMLFormElement>(null);
  const timedOut = useRef(false);

  const baseUrl = environment === 'production'
    ? 'https://production.wompi.co'
    : 'https://sandbox.wompi.co';

  // [INVARIANT 14] 30-second timeout — clear on unmount
  useEffect(() => {
    const timer = setTimeout(() => {
      timedOut.current = true;
      onError('La sesión de pago expiró. Por favor intenta de nuevo.');
    }, 30_000);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || timedOut.current) return;

    const form = formRef.current;
    if (!form) return;

    // [INVARIANT 13] Read card fields from uncontrolled DOM inputs — never store in state
    const data = new FormData(form);
    const number = (data.get('number') as string ?? '').replace(/\s/g, '');
    const cvc = data.get('cvc') as string ?? '';
    const exp_month = data.get('exp_month') as string ?? '';
    // Wompi requires 2-digit year (e.g. "29" not "2029")
    const exp_year_raw = data.get('exp_year') as string ?? '';
    const exp_year = exp_year_raw.length === 4 ? exp_year_raw.slice(2) : exp_year_raw;
    const card_holder = data.get('card_holder') as string ?? '';

    // Basic client-side validation (no storage — values are transient locals)
    if (!number || number.length < 12) { setFieldError('Número de tarjeta no válido.'); return; }
    if (!cvc || cvc.length < 3) { setFieldError('Código de seguridad no válido.'); return; }
    if (!exp_month || exp_month.length !== 2 || !exp_year || exp_year.length !== 2) { setFieldError('Fecha de vencimiento no válida.'); return; }
    if (!card_holder.trim()) { setFieldError('Nombre del titular requerido.'); return; }

    setFieldError(null);
    setSubmitting(true);

    try {
      // [INVARIANT 12] POST directly to Wompi from browser — card data NEVER touches our backend
      const res = await fetch(`${baseUrl}/v1/tokens/cards`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${publicKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ number, cvc, exp_month, exp_year, card_holder }),
      });

      // [INVARIANT 13] Immediately discard card field values — only keep safe token metadata
      // Wompi response envelope: { status: 'CREATED', data: { id, status: 'VALID', brand, last_four, ... } }
      const payload = await res.json() as {
        status?: string;
        data?: {
          id?: string;
          status?: string;
          brand?: string;
          last_four?: string;
          exp_month?: string;
          exp_year?: string;
        };
        error?: { type: string; messages: Record<string, string[]> };
      };

      const token = payload.data;
      if (!res.ok || payload.status !== 'CREATED' || !token?.id) {
        // Extract first validation message if present
        const firstMsg = payload.error?.messages
          ? Object.values(payload.error.messages).flat()[0]
          : undefined;
        throw new Error(firstMsg ?? `Tokenization failed: ${res.status}`);
      }

      // Only safe metadata returned — NO PAN/CVV in result
      const tokenResult: CardTokenResult = {
        payment_method_id: token.id,
        last4: token.last_four,
        brand: token.brand,
        exp_month: token.exp_month,
        exp_year: token.exp_year,
      };

      onSuccess(tokenResult);
    } catch (err) {
      setSubmitting(false);
      onError(mapTokenizationError(err));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-start p-4 pt-12">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Pago con tarjeta</h2>
            <button
              type="button"
              onClick={onCancel}
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              ← Volver
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Tus datos son procesados de forma segura por Wompi. Orangepill nunca ve tu número de tarjeta.
          </p>
        </div>

        {/* Card form — all inputs are UNCONTROLLED (no React state for card fields) */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          {/* Lock icon + PCI note */}
          <div className="flex items-center gap-2 mb-5 text-xs text-green-700 bg-green-50 rounded-lg px-3 py-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span>Conexión segura — datos cifrados por Wompi</span>
          </div>

          <form ref={formRef} onSubmit={handleSubmit} autoComplete="cc-on">
            {/* Card number */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span>Número de tarjeta</span>
                {brand !== 'unknown' && (
                  <span className="ml-2 text-xs font-semibold text-brand-600 uppercase">{brand}</span>
                )}
              </label>
              <input
                name="number"
                type="tel"
                inputMode="numeric"
                autoComplete="cc-number"
                maxLength={brand === 'amex' ? 17 : 19}
                placeholder="0000 0000 0000 0000"
                required
                disabled={submitting}
                value={cardDisplay}
                onChange={(e) => {
                  const detected = detectBrand(e.target.value.replace(/\D/g, ''));
                  setBrand(detected);
                  setCardDisplay(formatCardNumber(e.target.value, detected));
                }}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl font-mono text-gray-900 placeholder-gray-400 focus:outline-none focus:border-brand-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
              />
            </div>

            {/* Card holder */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre en la tarjeta
              </label>
              <input
                name="card_holder"
                type="text"
                autoComplete="cc-name"
                placeholder="Como aparece en la tarjeta"
                required
                disabled={submitting}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-brand-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
              />
            </div>

            {/* Expiry + CVC */}
            <div className="flex gap-3 mb-5">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mes (MM)
                </label>
                <input
                  name="exp_month"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="cc-exp-month"
                  maxLength={2}
                  placeholder="MM"
                  required
                  disabled={submitting}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl font-mono text-gray-900 placeholder-gray-400 focus:outline-none focus:border-brand-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Año (AA)
                </label>
                <input
                  name="exp_year"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="cc-exp-year"
                  maxLength={2}
                  placeholder="AA"
                  required
                  disabled={submitting}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl font-mono text-gray-900 placeholder-gray-400 focus:outline-none focus:border-brand-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CVC
                </label>
                <input
                  name="cvc"
                  type="password"
                  inputMode="numeric"
                  autoComplete="cc-csc"
                  maxLength={brand === 'amex' ? 4 : 3}
                  placeholder="•••"
                  required
                  disabled={submitting}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl font-mono text-gray-900 placeholder-gray-400 focus:outline-none focus:border-brand-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>

            {/* Validation error (local) or persisted tokenization error (from state machine) */}
            {(fieldError || persistentError) && (
              <div className="mb-4 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                {fieldError ?? persistentError}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-brand-500 hover:bg-brand-600 disabled:bg-brand-300 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-xl transition-colors"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Procesando...
                </span>
              ) : (
                'Pagar con tarjeta'
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          Pago procesado de forma segura por Orangepill vía Wompi
        </p>
      </div>
    </div>
  );
}
